// src/features/s-curve/lib/wbsTree.ts

export function collectDescendants(rootKey: string, childrenMap: Map<string, string[]>) {
    const out: string[] = [];
    const stack = [...(childrenMap.get(rootKey) ?? [])];

    // ✅ pop/push DFS (shift/unshift 제거)
    while (stack.length) {
        const k = stack.pop()!;
        out.push(k);

        const kids = childrenMap.get(k);
        if (kids?.length) stack.push(...kids);
    }

    return out;
}

export type DraftTreeIndex<T> = {
    byKey: Map<string, T>;
    keyToParentKey: Map<string, string | null>;
    childrenMap: Map<string, string[]>;
};

type BuildDraftTreeIndexOpts<T> = {
    getKey: (it: T) => string;

    // nodeId 기반 parent 연결 (필요 시)
    getNodeId?: (it: T) => string | null | undefined;
    getParentNodeId?: (it: T) => string | null | undefined;

    // clientId 기반 parent 연결 (필요 시)
    getClientId?: (it: T) => string | null | undefined;
    getParentClientId?: (it: T) => string | null | undefined;
};

/**
 * ✅ DraftItem/Row 등 “key + (nodeId|clientId) + parent(nodeId|clientId)” 구조면 공통으로 인덱싱 가능
 */
export function buildDraftTreeIndex<T>(items: T[], opts: BuildDraftTreeIndexOpts<T>): DraftTreeIndex<T> {
    const byKey = new Map<string, T>();

    const nodeIdToKey = new Map<string, string>();
    const clientIdToKey = new Map<string, string>();

    for (const it of items) {
        const k = opts.getKey(it);
        byKey.set(k, it);

        const nodeId = opts.getNodeId?.(it);
        if (nodeId) nodeIdToKey.set(nodeId, k);

        const cid = opts.getClientId?.(it);
        if (cid) clientIdToKey.set(cid, k);
    }

    const keyToParentKey = new Map<string, string | null>();
    const childrenMap = new Map<string, string[]>();

    for (const it of items) {
        const childKey = opts.getKey(it);

        const parentNodeId = opts.getParentNodeId?.(it) ?? null;
        const parentClientId = opts.getParentClientId?.(it) ?? null;

        const parentKey =
            (parentNodeId && nodeIdToKey.get(parentNodeId)) ||
            (parentClientId && clientIdToKey.get(parentClientId)) ||
            null;

        keyToParentKey.set(childKey, parentKey);

        if (parentKey) {
            const arr = childrenMap.get(parentKey) ?? [];
            arr.push(childKey);
            childrenMap.set(parentKey, arr);
        }
    }

    return { byKey, keyToParentKey, childrenMap };
}

export type OrderedTreeIndex<Row> = {
    ordered: Row[];
    childrenMap: Map<string, string[]>;
    roots: string[];
};

type BuildOrderedTreeIndexOpts<Row> = {
    getKey: (r: Row) => string;
    getNodeId: (r: Row) => string;
    getParentNodeId: (r: Row) => string | null;
    getOrderIndex?: (r: Row) => number | null | undefined;
};

/**
 * ✅ SCurveWbsList에서 쓰는 “preorder 정렬 + childrenMap + roots” 생성 로직 공통화
 */
export function buildOrderedTreeIndex<Row>(rows: Row[], opts: BuildOrderedTreeIndexOpts<Row>): OrderedTreeIndex<Row> {
    const keyByNodeId = new Map<string, string>();
    for (const r of rows) keyByNodeId.set(opts.getNodeId(r), opts.getKey(r));

    const rowByKey = new Map<string, Row>();
    for (const r of rows) rowByKey.set(opts.getKey(r), r);

    const basePos = new Map<string, number>();
    rows.forEach((r, i) => basePos.set(opts.getKey(r), i));

    const childrenMap = new Map<string, string[]>();
    const parentKeyMap = new Map<string, string | null>();

    for (const r of rows) {
        const key = opts.getKey(r);
        const parentNodeId = opts.getParentNodeId(r);
        const parentKey = parentNodeId ? (keyByNodeId.get(parentNodeId) ?? null) : null;

        parentKeyMap.set(key, parentKey);

        if (parentKey) {
            const arr = childrenMap.get(parentKey) ?? [];
            arr.push(key);
            childrenMap.set(parentKey, arr);
        }
    }

    const siblingSort = (aKey: string, bKey: string) => {
        const a = rowByKey.get(aKey);
        const b = rowByKey.get(bKey);

        const ai = a ? (opts.getOrderIndex?.(a) ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
        const bi = b ? (opts.getOrderIndex?.(b) ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;

        if (ai !== bi) return ai - bi;
        return (basePos.get(aKey) ?? 0) - (basePos.get(bKey) ?? 0);
    };

    const roots = rows
        .map((r) => opts.getKey(r))
        .filter((k) => (parentKeyMap.get(k) ?? null) == null)
        .sort(siblingSort);

    for (const [p, kids] of childrenMap.entries()) {
        kids.sort(siblingSort);
        childrenMap.set(p, kids);
    }

    const ordered: Row[] = [];
    const visited = new Set<string>();

    const walk = (key: string) => {
        if (visited.has(key)) return;
        visited.add(key);

        const r = rowByKey.get(key);
        if (r) ordered.push(r);

        const kids = childrenMap.get(key) ?? [];
        for (const ck of kids) walk(ck);
    };

    for (const rk of roots) walk(rk);

    // 누락 방지
    for (const r of rows) {
        const k = opts.getKey(r);
        if (!visited.has(k)) ordered.push(r);
    }

    return { ordered, childrenMap, roots };
}
